import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { useAuth } from './AuthContext';
import { getSparksState } from '../api/sparksApi';
import { celebrationsFromRows, enqueueCelebrations } from '../components/Sparks/achievementRules';
import { readNavCache, writeNavCache } from '../components/Sparks/navCache';

const SparksContext = createContext(null);

// Long enough for the question-page flight (1.5 s + stagger) to finish before a pop is dropped.
const POP_MS = 2600;
// Coming back to the app reloads the state (draining overnight freeze saves) on a new UK day, or after this long.
const RETURN_RELOAD_MS = 10 * 60 * 1000;
const ukDay = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
const CAP_LABELS = {
  rewardedCorrect: "Addy's Sparks jar is full for today — come back tomorrow!",
  completions: "Addy's Sparks jar is full for today — come back tomorrow!",
  worksheets: 'Two worksheets a day count for Sparks — the rest is still great practice!',
};

const EMPTY_STATE = {
  loaded: false, // true once the child's state has come back from the API
  navReady: false, // the nav chips have values to draw: the state, or the last known one (navCache.js)
  enabled: true,
  balance: 0,
  lifetime: 0,
  equipped: {},
  owned: [],
  achievements: [],
  level: 1,
  nextLevelAt: null,
  levelThresholds: [],
  levelBrackets: [],
  levelHistory: [],
  dailyStreak: 0,
  streakFreezes: 0,
  freezeCap: 3,
  streakBadges: [],
  nextMilestone: null,
  otherAchievements: [],
  earnRates: null,
};

/**
 * Sparks balance, level and streak, the live "+N" pop-up queue and the celebration cards
 * (streak, freeze, streak badge, level-up) for the logged-in child (Sparks design §2.6, levels plan §7).
 * Awards never originate here: activity responses carry `sparksAwarded` / `sparksBalance` and
 * screens hand them to `pushAwards`. Loading the state drains the server's pending-events queue
 * (awards made while the child was not waiting on a reply, e.g. a tutor shout-out).
 */
export function SparksProvider({ children }) {
  const { loggedInChild } = useAuth();
  const childId = loggedInChild?._id;
  const [state, setState] = useState(() => ({ ...EMPTY_STATE, ...readNavCache(childId) }));
  const [pops, setPops] = useState([]);
  const [capNotice, setCapNotice] = useState(null);
  const [celebrations, setCelebrations] = useState([]);
  // True while the question page's Addy burst is mounted: it draws the bubbles, so the nav chip must not.
  const [burstMounted, setBurstMounted] = useState(false);
  // The end-of-lesson recap runs its own sequence: it mutes the nav pops (it counts the Sparks up itself).
  const [recapOpen, setRecapOpen] = useState(false);
  // Celebration cards only ever play at the end of an activity: they wait, wherever they came from (a reply, the
  // pending queue at login or in class), until the recap lets them out after its count-up.
  const [cardsReleased, setCardsReleased] = useState(false);
  // "Something new to buy" (the nav sticker and bubble) waits until the recap's last stage has played.
  const [shopNewsHeld, setShopNewsHeld] = useState(false);
  const holdRef = useRef(false);
  holdRef.current = burstMounted || !cardsReleased;
  const streakRef = useRef(0);
  streakRef.current = state.dailyStreak;
  const popId = useRef(0);
  const lastChild = useRef(null);
  const lastLoad = useRef(null);

  const queueCards = useCallback((cards) => {
    if (!cards.length) return;
    const now = Date.now();
    const withIds = cards.map((c) => ({ ...c, id: `${now}-${popId.current++}` }));
    setCelebrations((prev) => enqueueCelebrations(prev, withIds, prev.length > 0 && !holdRef.current));
  }, []);

  /** `fromState`: the rows were drained by a state load that already counted them in lifetime. */
  const pushAwards = useCallback((awarded, balance, capReached, fromState = false) => {
    const all = Array.isArray(awarded) ? awarded.filter(Boolean) : [];
    const rows = all.filter((a) => Number(a.amount) > 0);
    if (rows.length) {
      const now = Date.now();
      setPops((prev) => [...prev, ...rows.map((a, i) => ({ id: `${now}-${popId.current++}`, amount: a.amount, label: a.label || '✨', delay: i * 350 }))]);
      rows.forEach((a) => posthog.capture('sparks_earned', { event: a.event, amount: a.amount, balanceAfter: balance ?? null }));
    }
    const cards = celebrationsFromRows(all);
    queueCards(cards);
    const levelTo = Math.max(0, ...cards.filter((c) => c.type === 'level').map((c) => c.to));
    const earnedKeys = new Set(cards.filter((c) => c.type === 'streak-badge').map((c) => c.key));
    if (levelTo || earnedKeys.size) {
      setState((s) => ({
        ...s,
        level: Math.max(s.level, levelTo),
        nextLevelAt: levelTo > s.level ? s.levelThresholds[levelTo] ?? null : s.nextLevelAt,
        streakBadges: s.streakBadges.map((b) => (earnedKeys.has(b.key) && !b.earnedAt ? { ...b, earnedAt: new Date().toISOString() } : b)),
      }));
    }
    // Replies carry the balance but not lifetime (XP): add what was earned so level progress stays current.
    const earned = fromState ? 0 : rows.filter((a) => a.event !== 'shop.refund').reduce((sum, a) => sum + Number(a.amount), 0);
    if (typeof balance === 'number' || earned) {
      setState((s) => ({ ...s, ...(typeof balance === 'number' ? { balance } : {}), lifetime: s.lifetime + earned }));
    }
    if (Array.isArray(capReached) && capReached.length) {
      setCapNotice(CAP_LABELS[capReached[0]] || CAP_LABELS.rewardedCorrect);
      posthog.capture('sparks_cap_reached', { cap: capReached[0] });
    }
  }, [queueCards]);

  /** Convenience for API responses: picks the Sparks fields off any activity reply. */
  const pushFromResponse = useCallback((res) => {
    if (!res) return;
    pushAwards(res.sparksAwarded, res.sparksBalance, res.sparksCapReached);
  }, [pushAwards]);

  const refreshState = useCallback(async () => {
    if (!childId) return null;
    lastLoad.current = { at: Date.now(), day: ukDay() };
    try {
      const { pendingEvents, ...s } = await getSparksState(childId);
      setState((prev) => ({ ...prev, ...s, loaded: true, navReady: true }));
      if (pendingEvents?.length) pushAwards(pendingEvents, s.balance, [], true);
      return s;
    } catch {
      return null; // non-fatal: the chip just keeps its last value
    }
  }, [childId, pushAwards]);

  /**
   * Handles a POST /children/:id/streak-activity reply: the streak card when the streak grew, then
   * any badge, freeze or level rows. A completion the server already counted (quiz, worksheet) left
   * its rows in the pending queue instead, so the state is reloaded to drain them.
   */
  const pushStreakResult = useCallback((result) => {
    if (!result || typeof result.dailyStreak !== 'number') return;
    const prev = streakRef.current;
    if (result.dailyStreak > prev) {
      const day = (result.sparksAwarded || []).find((a) => a?.event === 'streak.day');
      queueCards([{ type: 'streak-day', streak: result.dailyStreak, amount: day?.amount ?? 0 }]);
    }
    setState((s) => ({ ...s, dailyStreak: result.dailyStreak, streakFreezes: result.streakFreezes ?? s.streakFreezes }));
    pushFromResponse(result);
    refreshState();
  }, [queueCards, pushFromResponse, refreshState]);

  const dismissCelebration = useCallback(() => setCelebrations((prev) => prev.slice(1)), []);

  useEffect(() => {
    if (childId && childId !== lastChild.current) {
      lastChild.current = childId;
      setPops([]);
      setCelebrations([]);
      setState({ ...EMPTY_STATE, ...readNavCache(childId) });
      refreshState();
    }
    if (!childId) lastChild.current = null;
  }, [childId, refreshState]);

  // A child who stays logged in never reloads the page: refresh when they come back to the tab, so the
  // balance is current and a freeze spent overnight is queued for their next recap.
  useEffect(() => {
    if (!childId) return undefined;
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const last = lastLoad.current;
      if (last && last.day === ukDay() && Date.now() - last.at < RETURN_RELOAD_MS) return;
      refreshState();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [childId, refreshState]);

  useEffect(() => {
    if (childId && state.loaded) writeNavCache(childId, state);
  }, [childId, state]);

  // Expire pop-ups.
  useEffect(() => {
    if (!pops.length) return undefined;
    const t = setTimeout(() => setPops((prev) => prev.slice(1)), POP_MS + (pops[0]?.delay || 0));
    return () => clearTimeout(t);
  }, [pops]);

  useEffect(() => {
    if (!capNotice) return undefined;
    const t = setTimeout(() => setCapNotice(null), 5000);
    return () => clearTimeout(t);
  }, [capNotice]);

  const value = useMemo(
    () => ({
      ...state,
      pops,
      capNotice,
      celebrations,
      burstMounted,
      setBurstMounted,
      recapOpen,
      setRecapOpen,
      cardsReleased,
      setCardsReleased,
      shopNewsHeld,
      setShopNewsHeld,
      pushAwards,
      pushFromResponse,
      pushStreakResult,
      dismissCelebration,
      refreshState,
      setBalance: (balance) => setState((s) => ({ ...s, balance })),
      setEquipped: (equipped) => setState((s) => ({ ...s, equipped })),
    }),
    [state, pops, capNotice, celebrations, burstMounted, recapOpen, cardsReleased, shopNewsHeld, pushAwards, pushFromResponse, pushStreakResult, dismissCelebration, refreshState],
  );

  return <SparksContext.Provider value={value}>{children}</SparksContext.Provider>;
}

const NOOP = {
  ...EMPTY_STATE,
  pops: [],
  capNotice: null,
  celebrations: [],
  burstMounted: false,
  setBurstMounted: () => {},
  recapOpen: false,
  setRecapOpen: () => {},
  cardsReleased: false,
  setCardsReleased: () => {},
  shopNewsHeld: false,
  setShopNewsHeld: () => {},
  pushAwards: () => {},
  pushFromResponse: () => {},
  pushStreakResult: () => {},
  dismissCelebration: () => {},
  refreshState: async () => null,
  setBalance: () => {},
  setEquipped: () => {},
};

/** Safe outside the provider (e.g. tutor preview renders) — returns inert no-ops. */
export function useSparks() {
  return useContext(SparksContext) || NOOP;
}
